import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Calendar, BookOpen, ChevronDown, ChevronUp, CheckCircle, Clock, FileText, Zap, Plus, Save, Edit2, Trash2, ChevronLeft, Upload, X, ArrowLeft, ArrowRight, AlertTriangle, CheckSquare, HelpCircle, Share2, ListChecks, Loader2 } from 'lucide-react';
import { useSelector } from 'react-redux';
import { createExamWithAI } from './api';
import { postTeacherExamFile } from '../postFileAws';
import { postAws } from '../postAws';

const Card = ({ children, className = "", onClick }) => (
  <div className={`bg-white rounded-xl shadow-lg p-4 ${className}`} onClick={onClick}>
    {children}
  </div>
);

const Button = ({ children, onClick, variant = "default", className = "", disabled = false, icon }) => {
  const baseClasses = "px-3 py-1.5 rounded-md font-medium transition-all duration-200 flex items-center justify-center text-sm";
  const variantClasses = {
    default: "bg-blue-500 text-white hover:bg-blue-600 disabled:bg-blue-300",
    outline: "border border-gray-300 text-gray-700 hover:bg-gray-100 disabled:bg-gray-100 disabled:text-gray-400",
    ghost: "text-gray-700 hover:bg-gray-100 disabled:text-gray-400",
  };

  return (
    <button
      className={`${baseClasses} ${variantClasses[variant]} ${className}`}
      onClick={onClick}
      disabled={disabled}
    >
      {icon && <span className="mr-1.5">{icon}</span>}
      {children}
    </button>
  );
};

const Input = ({ id, type = "text", placeholder, value, onChange, className = "" }) => (
  <input
    id={id}
    type={type}
    placeholder={placeholder}
    value={value}
    onChange={onChange}
    className={`w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${className}`}
  />
);

const Textarea = ({ id, placeholder, value, onChange, className = "" }) => (
  <textarea
    id={id}
    placeholder={placeholder}
    value={value}
    onChange={onChange}
    className={`w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${className}`}
  />
);

const Label = ({ htmlFor, children }) => (
  <label htmlFor={htmlFor} className="block text-sm font-medium text-gray-700 mb-1">
    {children}
  </label>
);

const AccordionItem = ({ title, children, defaultOpen = false }) => {
  const [isOpen, setIsOpen] = useState(defaultOpen);

  return (
    <div className="border rounded-md mb-2">
      <button
        className="w-full text-left p-3 flex justify-between items-center focus:outline-none"
        onClick={() => setIsOpen(!isOpen)}
      >
        <span className="font-medium">{title}</span>
        {isOpen ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
      </button>
      {isOpen && <div className="p-3 border-t">{children}</div>}
    </div>
  );
};

const Flashcard = ({ message, onClose }) => (
  <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
    <div className="bg-white p-6 rounded-lg shadow-xl max-w-md w-full">
      <p className="text-lg font-semibold mb-4">{message}</p>
      <Button onClick={onClose} className="w-full">Close</Button>
    </div>
  </div>
);

const Loader = () => (
  <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
    <div className="bg-white p-6 rounded-lg shadow-xl">
      <Loader2 className="animate-spin h-8 w-8 text-blue-500" />
    </div>
  </div>
);

function ImageUpload({ onImageUpload, onImageRemove, currentImage }) {
  const fileInputRef = useRef(null);

  const handleFileChange = (event) => {
    const file = event.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        onImageUpload(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  return (
    <div className="mt-2">
      {currentImage ? (
        <div className="relative inline-block">
          <img src={currentImage} alt="Uploaded" className="max-w-xs max-h-40 rounded-md" />
          <button
            onClick={onImageRemove}
            className="absolute top-0 right-0 bg-red-500 text-white p-1 rounded-full"
          >
            <X size={16} />
          </button>
        </div>
      ) : (
        <Button
          variant="outline"
          onClick={() => fileInputRef.current?.click()}
          icon={<Upload size={16} />}
        >
          Upload Image
        </Button>
      )}
      <input
        type="file"
        ref={fileInputRef}
        onChange={handleFileChange}
        accept="image/*"
        className="hidden"
      />
    </div>
  );
}

function QuestionEditor({ question, index, onUpdate, onDelete, isNew }) {
  const [isExpanded, setIsExpanded] = useState(isNew);
  const [localQuestion, setLocalQuestion] = useState(question);

  const handleChange = (field, value) => {
    setLocalQuestion({ ...localQuestion, [field]: value });
  };

  const handleSave = () => {
    onUpdate(localQuestion);
    setIsExpanded(false);
  };

  const handleImageUpload = (imageData) => {
    handleChange('image', imageData);
  };

  const handleImageRemove = () => {
    handleChange('image', null);
  };

  return (
    <div className="border p-3 rounded-md bg-white shadow-sm transition-all duration-300 hover:shadow-md">
      <div className="flex justify-between items-center">
        <h3 className="font-medium text-lg">Question {index + 1}</h3>
        <div className="space-x-2">
          {!isNew && (
            <Button variant="ghost" onClick={() => setIsExpanded(!isExpanded)} icon={isExpanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}>
              {isExpanded ? "Collapse" : "Expand"}
            </Button>
          )}
          <Button variant="ghost" onClick={onDelete} icon={<Trash2 size={16} />}>
            Delete
          </Button>
        </div>
      </div>
      {isExpanded && (
        <div className="mt-4 space-y-4">
          <div>
            <Label htmlFor={`question-${index}`}>Question Text</Label>
            <Textarea
              id={`question-${index}`}
              value={localQuestion.question}
              onChange={(e) => handleChange('question', e.target.value)}
              placeholder="Enter question text"
              className="min-h-[80px]"
            />
          </div>
          <div className="flex items-center space-x-4">
            <div className="flex-1">
              <Label htmlFor={`question-${index}-type`}>Question Type</Label>
              <select
                id={`question-${index}-type`}
                value={localQuestion.questionType}
                onChange={(e) => handleChange('questionType', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="multiple-choice">Multiple Choice</option>
                <option value="short-answer">Short Answer</option>
                <option value="essay">Essay</option>
              </select>
            </div>
            <div className="flex-1">
              <Label htmlFor={`question-${index}-marks`}>Marks</Label>
              <Input
                id={`question-${index}-marks`}
                type="number"
                value={localQuestion.marks || ''}
                onChange={(e) => handleChange('marks', parseInt(e.target.value))}
                placeholder="Enter marks"
              />
            </div>
            <div className="flex-1">
              <Label>Question Image</Label>
              <ImageUpload
                onImageUpload={handleImageUpload}
                onImageRemove={handleImageRemove}
                currentImage={localQuestion.image}
              />
            </div>
          </div>
          {localQuestion.questionType === 'multiple-choice' && (
            <div className="space-y-2">
              <Label>Options</Label>
              <div className="grid grid-cols-2 gap-2">
                {(localQuestion.mcqOptions || []).map((option, optionIndex) => (
                  <div key={optionIndex} className="flex items-center space-x-2">
                    <Input
                      value={option.text}
                      onChange={(e) => {
                        const newOptions = [...localQuestion.mcqOptions];
                        newOptions[optionIndex] = { ...newOptions[optionIndex], text: e.target.value };
                        handleChange('mcqOptions', newOptions);
                      }}
                      placeholder={`Option ${optionIndex + 1}`}
                    />
                    <input
                      type="checkbox"
                      checked={option.isCorrect}
                      onChange={(e) => {
                        const newOptions = [...localQuestion.mcqOptions];
                        newOptions[optionIndex] = { ...newOptions[optionIndex], isCorrect: e.target.checked };
                        handleChange('mcqOptions', newOptions);
                      }}
                      className="form-checkbox h-5 w-5 text-blue-600"
                    />
                  </div>
                ))}
              </div>
              <Button
                variant="outline"
                onClick={() => handleChange('mcqOptions', [...(localQuestion.mcqOptions || []), { optionId: '', text: '', isCorrect: false }])}
                icon={<Plus size={16} />}
              >
                Add Option
              </Button>
            </div>
          )}
          {localQuestion.questionType !== 'multiple-choice' && (
            <div>
              <Label htmlFor={`question-${index}-answer`}>Correct Answer</Label>
              <Textarea
                id={`question-${index}-answer`}
                value={localQuestion.correctAnswer || ''}
                onChange={(e) => handleChange('correctAnswer', e.target.value)}
                placeholder="Enter correct answer"
                className="min-h-[80px]"
              />
            </div>
          )}
          {localQuestion.explanation && (
            <div>
              <Label htmlFor={`question-${index}-explanation`}>Explanation</Label>
              <Textarea
                id={`question-${index}-explanation`}
                value={localQuestion.explanation}
                onChange={(e) => handleChange('explanation', e.target.value)}
                placeholder="Enter explanation"
                className="min-h-[80px]"
              />
            </div>
          )}
          <Button onClick={handleSave} icon={<Save size={16} />}>Save Question</Button>
        </div>
      )}
    </div>
  );
}

function AIQuestionGenerator({ onGenerate, currentSectionName, examTitle, classValue }) {
  const [prompt, setPrompt] = useState('');
  const [questionCounts, setQuestionCounts] = useState({
    'multiple-choice': 0,
    'short-answer': 0,
    'essay': 0
  });
  const [difficulty, setDifficulty] = useState('medium');
  const [referenceMaterial, setReferenceMaterial] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const fileInputRef = useRef(null);

  const handleGenerate = async () => {
    setIsLoading(true);
    setError(null);
    try {
      let refMaterialUri = null;
      let referenceType = null;

      if (referenceMaterial) {
        const teacherId = localStorage.getItem("teacherId");
        const response = await postTeacherExamFile(referenceMaterial, teacherId, examTitle, 'refMaterial');
        refMaterialUri = response.uri;
        referenceType = referenceMaterial.type.startsWith('image/') ? 'image' : 'pdf';
      }

      const examData = {
        topic: prompt,
        questionTypeCounts: {
          multipleChoice: questionCounts['multiple-choice'],
          shortAnswer: questionCounts['short-answer'],
          essay: questionCounts['essay']
        },
        questionDifficulty: difficulty,
        refMaterial: refMaterialUri,
        classValue: classValue,
        referenceType: referenceType,
        examTitle: examTitle
      };

      const response = await createExamWithAI(examData);
      console.log('AI-generated exam response:', response);

      if (response && response.questionArray && Array.isArray(response.questionArray)) {
        onGenerate(response.questionArray);
      } else {
        throw new Error('Invalid question array in response');
      }
    } catch (err) {
      setError('Failed to generate questions: ' + err.message);
      console.error(err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setReferenceMaterial(file);
    }
  };

  return (
    <Card className="space-y-4">
      <h3 className="text-xl font-semibold text-blue-800">AI Question Generator</h3>
      <p className="text-sm text-gray-600">Generating questions for: {currentSectionName}</p>
      <div>
        <Label htmlFor="ai-prompt">AI Prompt</Label>
        <Textarea
          id="ai-prompt"
          value={prompt}
          onChange={(e) => setPrompt(e.target.value)}
          placeholder="Enter a prompt for AI to generate questions"
          className="min-h-[100px]"
        />
      </div>
      <div className="space-y-4">
        <Label>Number of Questions</Label>
        <div className="grid grid-cols-3 gap-4">
          {Object.entries(questionCounts).map(([type, count]) => (
            <div key={type} className="flex flex-col space-y-1">
              <Label htmlFor={`count-${type}`}>{type.replace('-', ' ')}</Label>
              <Input
                id={`count-${type}`}
                type="number"
                value={count}
                onChange={(e) => setQuestionCounts({...questionCounts, [type]: parseInt(e.target.value) || 0})}
                min="0"
              />
            </div>
          ))}
        </div>
      </div>
      <div>
        <Label htmlFor="difficulty">Difficulty Level</Label>
        <select
          id="difficulty"
          value={difficulty}
          onChange={(e) => setDifficulty(e.target.value)}
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
        >
          <option value="easy">Easy</option>
          <option value="medium">Medium</option>
          <option value="hard">Hard</option>
        </select>
      </div>
      <div>
        <Label>Reference Material</Label>
        <div className="flex items-center space-x-2">
          <Button
            variant="outline"
            onClick={() => fileInputRef.current?.click()}
            icon={<Upload size={16} />}
          >
            Upload File
          </Button>
          {referenceMaterial && (
            <div className="flex items-center space-x-2 bg-blue-100 px-3 py-2 rounded-md">
              <span className="text-sm truncate">{referenceMaterial.name}</span>
              <button
                onClick={() => setReferenceMaterial(null)}
                className="text-blue-500 hover:text-blue-700"
              >
                <X size={16} />
              </button>
            </div>
          )}
        </div>
        <input
          type="file"
          ref={fileInputRef}
          onChange={handleFileChange}
          accept=".pdf,image/*"
          className="hidden"
        />
      </div>
      {error && <p className="text-red-500">{error}</p>}
      <Button
        onClick={handleGenerate}
        disabled={isLoading || !prompt || Object.values(questionCounts).every(count => count === 0)}
        icon={isLoading ? <span className="animate-spin">⌛</span> : <Zap size={16} />}
        className="w-full bg-gradient-to-r from-blue-500 to-indigo-600 hover:from-blue-600 hover:to-indigo-700"
      >
        {isLoading ? 'Generating...' : 'Generate Questions'}
      </Button>
    </Card>
  );
}

export default function ExamDescription() {
  const navigate = useNavigate();
  const [examTitle, setExamTitle] = useState("");
  const [examDescription, setExamDescription] = useState("");
  const [sections, setSections] = useState([]);
  const [currentStep, setCurrentStep] = useState("setup");
  const [showAIGenerator, setShowAIGenerator] = useState(false);
  const [currentSectionIndex, setCurrentSectionIndex] = useState(0);
  const [editingSectionId, setEditingSectionId] = useState(null);
  const [classGrade, setClassGrade] = useState("");
  const [subject, setSubject] = useState("");
  const [classCode, setClassCode] = useState("");
  const [classRoomId, setClassRoomId] = useState("");

  const today = new Date();
  const threeDaysLater = new Date(today);
  threeDaysLater.setDate(today.getDate() + 3);

  const [startDate, setStartDate] = useState(today.toISOString().split('T')[0]);
  const [startTime, setStartTime] = useState("09:00");
  const [endDate, setEndDate] = useState(threeDaysLater.toISOString().split('T')[0]);
  const [endTime, setEndTime] = useState("09:00");
  const [duration, setDuration] = useState("120");
  const [instructions, setInstructions] = useState([]);
  const [questionTypeCounts, setQuestionTypeCounts] = useState({
    multipleChoice: 0,
    shortAnswer: 0,
    essay: 0
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [flashcardMessage, setFlashcardMessage] = useState(null);

  const selectedClass = useSelector(state => state.teacherSub.selectedClass);
  const selectedSubject = useSelector(state => state.teacherSub.selectedSubject);
  const teacherId = useSelector(state => state.teacherSub.teacherId);

  useEffect(() => {
    // Load exam data from localStorage if it exists
    const savedExamData = localStorage.getItem('examData');
    if (savedExamData) {
      const parsedExamData = JSON.parse(savedExamData);
      setExamTitle(parsedExamData.exam_topic || "");
      setExamDescription(parsedExamData.examDescription || "");
      setSections(parsedExamData.sections || []);
      setInstructions(parsedExamData.instructions || []);
      setQuestionTypeCounts(parsedExamData.questionTypeCounts || {
        multipleChoice: 0,
        shortAnswer: 0,
        essay: 0
      });
    }
    
    // Load classCode and classRoomId from localStorage
    setClassCode(localStorage.getItem('classCode') || "");
    setClassRoomId(localStorage.getItem('classRoomId') || "");
    setClassGrade(localStorage.getItem('class') || "");
    setSubject(localStorage.getItem('subject') || "");
  }, []);

  const handleSave = (step) => {
    console.log(`Saving ${step}...`);
    let dataToSave = {};
    switch (step) {
      case 'setup':
        if (!examTitle.trim() || !examDescription.trim()) {
          setFlashcardMessage("Please enter all fields.");
          return;
        }
        dataToSave = {
          exam_topic: examTitle,
          examDescription,
          examStartDate: `${startDate}T${startTime}:00`,
          examEndDate: `${endDate}T${endTime}:00`,
          examDuration: duration,
          class: classGrade,
          subject: subject,
          teacherId,
        };
        break;
      case 'instructions':
        if (instructions.length === 0) {
          setFlashcardMessage("Please add at least one instruction.");
          return;
        }
        dataToSave = {
          instructions,
        };
        break;
      case 'sections':
        if (sections.length === 0) {
          setFlashcardMessage("Please create at least one section.");
          return;
        }
        dataToSave = {
          sections,
          questionTypeCounts,
        };
        break;
      default:
        console.error('Invalid step for saving');
        return;
    }

    setLoading(true);
    setTimeout(() => {
      // Save to localStorage
      const existingData = JSON.parse(localStorage.getItem('examData') || '{}');
      const updatedData = { ...existingData, ...dataToSave };
      localStorage.setItem('examData', JSON.stringify(updatedData));
      setLoading(false);
      setFlashcardMessage("Information stored successfully.");
      console.log(`${step} saved successfully`);
    }, 2000);
  };

  const handleSaveAndShareExam = async () => {
    if (instructions.length === 0) {
      setFlashcardMessage("Please add at least one instruction before saving the exam.");
      return;
    }
    if (sections.length === 0) {
      setFlashcardMessage("Please create at least one section before saving the exam.");
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const examData = {
        body: {
          exam_topic: examTitle,
          examDescription,
          examStartDate: `${startDate}T${startTime}:00`,
          examEndDate: `${endDate}T${endTime}:00`,
          examDuration: duration,
          class: classGrade,
          subject: subject,
          teacherId,
          classCode,
          classRoomId,
          instructions,
          sections: sections.map(section => ({
            name: section.name,
            questions: section.questions.map(question => ({
              text: question.question,
              type: question.questionType,
              options: question.questionType === 'multiple-choice' ? question.mcqOptions.map(option => option.text) : [],
              marks: parseInt(question.marks),
              image: question.image
            }))
          })),
          questionTypeCounts: {
            multipleChoice: questionTypeCounts.multipleChoice,
            shortAnswer: questionTypeCounts.shortAnswer,
            essay: questionTypeCounts.essay
          }
        },
        request: "create-exam"
      };

      const response = await postAws('https://api-dev.aurait.ai/exam', "POST", examData);
      console.log('Server response:', response);

      if (response.statusCode === 201) {
        console.log('Exam saved and shared successfully');
        localStorage.removeItem('examData');
        navigate('/exams');
      } else {
        throw new Error(response.body ? JSON.parse(response.body).message : 'Failed to save and share exam');
      }
    } catch (error) {
      console.error('Error saving and sharing exam:', error);
      setError(error.message || 'Failed to save and share exam. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const addSection = () => {
    const newSection = {
      name: `Section ${sections.length + 1}`,
      questions: []
    };
    setSections([...sections, newSection]);
  };

  const updateSection = (index, newData) => {
    const updatedSections = [...sections];
    updatedSections[index] = { ...updatedSections[index], ...newData };
    setSections(updatedSections);
  };

  const deleteSection = (index) => {
    const updatedSections = sections.filter((_, i) => i !== index);
    setSections(updatedSections);
    if (currentSectionIndex >= updatedSections.length) {
      setCurrentSectionIndex(Math.max(0, updatedSections.length - 1));
    }
  };

  const addQuestion = (sectionIndex, method) => {
    const newQuestion = {
      question: "",
      questionType: "multiple-choice",
      mcqOptions: [
        { optionId: "A", text: "", isCorrect: false },
        { optionId: "B", text: "", isCorrect: false },
        { optionId: "C", text: "", isCorrect: false },
        { optionId: "D", text: "", isCorrect: false }
      ],
      method: method,
      marks: 1,
      image: null
    };
    const updatedSections = [...sections];
    updatedSections[sectionIndex].questions.push(newQuestion);
    setSections(updatedSections);
    updateQuestionTypeCounts(updatedSections);
  };

  const updateQuestion = (sectionIndex, questionIndex, newData) => {
    const updatedSections = [...sections];
    updatedSections[sectionIndex].questions[questionIndex] = { 
      ...updatedSections[sectionIndex].questions[questionIndex], 
      ...newData 
    };
    setSections(updatedSections);
    updateQuestionTypeCounts(updatedSections);
  };

  const deleteQuestion = (sectionIndex, questionIndex) => {
    const updatedSections = [...sections];
    updatedSections[sectionIndex].questions.splice(questionIndex, 1);
    setSections(updatedSections);
    updateQuestionTypeCounts(updatedSections);
  };

  const updateQuestionTypeCounts = (updatedSections) => {
    const allQuestions = updatedSections.flatMap(section => section.questions);
    const counts = {
      multipleChoice: allQuestions.filter(q => q.questionType === 'multiple-choice').length,
      shortAnswer: allQuestions.filter(q => q.questionType === 'short-answer').length,
      essay: allQuestions.filter(q => q.questionType === 'essay').length
    };
    setQuestionTypeCounts(counts);
  };

  const handleAIGenerate = (generatedQuestions) => {
    if (Array.isArray(generatedQuestions) && generatedQuestions.length > 0) {
      const formattedQuestions = generatedQuestions.map(q => ({
        question: q.question,
        questionType: q.questionType,
        mcqOptions: q.mcqOptions || [],
        method: "ai",
        marks: q.marks || 1,
        image: q.image || null,
        correctAnswer: q.correctAnswer || "",
        explanation: q.explanation || ""
      }));

      const updatedSections = [...sections];
      updatedSections[currentSectionIndex].questions = [
        ...updatedSections[currentSectionIndex].questions,
        ...formattedQuestions
      ];
      setSections(updatedSections);
      updateQuestionTypeCounts(updatedSections);
      console.log('AI-generated questions added:', formattedQuestions);
    } else {
      console.error('Generated questions are not in the expected format:', generatedQuestions);
    }
    setShowAIGenerator(false);
  };

  const renderStep = () => {
    switch (currentStep) {
      case "setup":
        return (
          <div className="space-y-6">
            <div className="space-y-4">
              <div>
                <Label htmlFor="title">Exam Title</Label>
                <Input
                  id="title"
                  placeholder="Enter exam title"
                  value={examTitle}
                  onChange={(e) => setExamTitle(e.target.value)}
                  className="bg-white"
                />
              </div>
              <div>
                <Label htmlFor="description">Exam Description</Label>
                <Textarea
                  id="description"
                  placeholder="Enter exam description"
                  value={examDescription}
                  onChange={(e) => setExamDescription(e.target.value)}
                  className="bg-white min-h-[100px]"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="startDate">Start Date</Label>
                  <Input
                    id="startDate"
                    type="date"
                    className="bg-white"
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                  />
                </div>
                <div>
                  <Label htmlFor="startTime">Start Time</Label>
                  <Input
                    id="startTime"
                    type="time"
                    className="bg-white"
                    value={startTime}
                    onChange={(e) => setStartTime(e.target.value)}
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="endDate">End Date</Label>
                  <Input
                    id="endDate"
                    type="date"
                    className="bg-white"
                    value={endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                  />
                </div>
                <div>
                  <Label htmlFor="endTime">End Time</Label>
                  <Input
                    id="endTime"
                    type="time"
                    className="bg-white"
                    value={endTime}
                    onChange={(e) => setEndTime(e.target.value)}
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="class">Class/Grade</Label>
                  <Input
                    id="class"
                    placeholder="Enter class or grade"
                    value={classGrade}
                    onChange={(e) => setClassGrade(e.target.value)}
                    className="bg-white"
                    readOnly
                  />
                </div>
                <div>
                  <Label htmlFor="subject">Subject</Label>
                  <Input
                    id="subject"
                    placeholder="Enter subject"
                    value={subject}
                    onChange={(e) => setSubject(e.target.value)}
                    className="bg-white"
                    readOnly
                  />
                </div>
              </div>
              <div>
                <Label htmlFor="duration">Duration (minutes)</Label>
                <Input
                  id="duration"
                  type="number"
                  placeholder="Enter duration"
                  className="bg-white"
                  value={duration}
                  onChange={(e) => setDuration(e.target.value)}
                />
              </div>
            </div>
            <Button onClick={() => handleSave('setup')} icon={<Save size={16} />}>Save Initial Setup</Button>
          </div>
        );
      case "instructions":
        return (
          <div className="space-y-6">
            <div>
              <Label htmlFor="instructions">Instructions</Label>
              {instructions.map((instruction, index) => (
                <div key={index} className="flex items-center mb-2">
                  <Input
                    value={instruction}
                    onChange={(e) => {
                      const newInstructions = [...instructions];
                      newInstructions[index] = e.target.value;
                      setInstructions(newInstructions);
                    }}
                    className="flex-grow mr-2"
                  />
                  <Button
                    variant="ghost"
                    onClick={() => {
                      const newInstructions = instructions.filter((_, i) => i !== index);
                      setInstructions(newInstructions);
                    }}
                    icon={<Trash2 size={16} />}
                  />
                </div>
              ))}
              <Button
                variant="outline"
                onClick={() => setInstructions([...instructions, ''])}
                icon={<Plus size={16} />}
                className="mt-2"
              >
                Add Instruction
              </Button>
            </div>
            <Button onClick={() => handleSave('instructions')} icon={<Save size={16} />}>Save Instructions</Button>
          </div>
        );
      case "sections":
        return (
          <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {sections.map((section, index) => (
                <Card
                  key={index}
                  className="cursor-pointer transition-all hover:shadow-lg"
                  onClick={() => setCurrentSectionIndex(index)}
                >
                  <div className="flex justify-between items-center mb-2">
                    {editingSectionId === index ? (
                      <input
                        type="text"
                        value={section.name}
                        onChange={(e) => updateSection(index, { name: e.target.value })}
                        onBlur={() => setEditingSectionId(null)}
                        autoFocus
                        className="text-lg font-semibold border-b border-gray-300 focus:outline-none focus:border-blue-500 w-full"
                      />
                    ) : (
                      <h3 className="text-lg font-semibold">{section.name}</h3>
                    )}
                    <div className="flex items-center space-x-2">
                      <Button
                        variant="ghost"
                        onClick={(e) => {
                          e.stopPropagation();
                          setEditingSectionId(index);
                        }}
                        icon={<Edit2 size={16} />}
                      />
                      <Button
                        variant="ghost"
                        onClick={(e) => {
                          e.stopPropagation();
                          deleteSection(index);
                        }}
                        icon={<Trash2 size={16} />}
                      />
                    </div>
                  </div>
                  <p className="text-sm text-gray-600 mb-2">{section.questions.length} questions</p>
                  <div className="flex justify-between">
                    <Button
                      variant="outline"
                      onClick={(e) => {
                        e.stopPropagation();
                        addQuestion(index, "manual");
                      }}
                      icon={<Plus size={16} />}
                    >
                      Add
                    </Button>
                    <Button
                      variant="outline"
                      onClick={(e) => {
                        e.stopPropagation();
                        setShowAIGenerator(true);
                        setCurrentSectionIndex(index);
                      }}
                      icon={<Zap size={16} />}
                    >
                      AI Generate
                    </Button>
                  </div>
                </Card>
              ))}
              <Card
                className="flex items-center justify-center cursor-pointer hover:bg-gray-100 transition-colors"
                onClick={addSection}
              >
                <Plus size={24} className="mr-2" />
                Add New Section
              </Card>
            </div>
            {sections.length > 0 && (
              <Card>
                <h3 className="text-xl font-semibold mb-4">{sections[currentSectionIndex].name}</h3>
                <div className="space-y-4">
                  {sections[currentSectionIndex].questions.map((question, qIndex) => (
                    <QuestionEditor
                      key={qIndex}
                      question={question}
                      index={qIndex}
                      onUpdate={(updatedQuestion) => updateQuestion(currentSectionIndex, qIndex, updatedQuestion)}
                      onDelete={() => deleteQuestion(currentSectionIndex, qIndex)}
                      isNew={false}
                    />
                  ))}
                </div>
                {showAIGenerator && (
                  <AIQuestionGenerator
                    onGenerate={handleAIGenerate}
                    currentSectionName={sections[currentSectionIndex].name}
                    examTitle={examTitle}
                    classValue={classGrade}
                  />
                )}
              </Card>
            )}
            <Button onClick={() => handleSave('sections')} icon={<Save size={16} />}>Save Sections</Button>
          </div>
        );
      case "summary":
        return (
          <Card>
            <h2 className="text-2xl font-semibold mb-4 text-orange-800">Exam Summary</h2>
            <div className="space-y-4">
              <AccordionItem title="1. Exam Details" defaultOpen={true}>
                <p><strong>Title:</strong> {examTitle}</p>
                <p><strong>Description:</strong> {examDescription}</p>
                <p><strong>Class/Grade:</strong> {classGrade}</p>
                <p><strong>Subject:</strong> {subject}</p>
                <p><strong>Start Date:</strong> {startDate} at {startTime}</p>
                <p><strong>End Date:</strong> {endDate} at {endTime}</p>
                <p><strong>Duration:</strong> {duration} minutes</p>
              </AccordionItem>
              <AccordionItem title="2. Instructions" defaultOpen={true}>
                <ul className="list-disc ml-6">
                  {instructions.map((instruction, index) => (
                    <li key={index}>{instruction}</li>
                  ))}
                </ul>
              </AccordionItem>
              <AccordionItem title="3. Question Type Counts" defaultOpen={true}>
                <p><strong>Multiple Choice:</strong> {questionTypeCounts.multipleChoice}</p>
                <p><strong>Short Answer:</strong> {questionTypeCounts.shortAnswer}</p>
                <p><strong>Essay:</strong> {questionTypeCounts.essay}</p>
              </AccordionItem>
              <AccordionItem title="4. Sections" defaultOpen={true}>
                {sections.map((section, index) => (
                  <AccordionItem key={index} title={section.name}>
                    <p>{section.questions.length} questions</p>
                    {section.questions.map((question, qIndex) => (
                      <div key={qIndex} className="ml-4 mt-2">
                        <p><strong>Q{qIndex + 1}:</strong> {question.question}</p>
                        {question.questionType === 'multiple-choice' && (
                          <ul className="list-disc ml-6">
                            {question.mcqOptions.map((option, oIndex) => (
                              <li key={oIndex}>{option.text} {option.isCorrect ? '(Correct)' : ''}</li>
                            ))}
                          </ul>
                        )}
                        <p><strong>Marks:</strong> {question.marks}</p>
                      </div>
                    ))}
                  </AccordionItem>
                ))}
              </AccordionItem>
              <div className="flex justify-end space-x-4 pt-4">
                <Button onClick={handleSaveAndShareExam} icon={<Share2 size={16} />}>Save & Share Exam</Button>
              </div>
            </div>
          </Card>
        );
    }
  };

  return (
    <div className="container mx-auto py-6 px-4 space-y-6 bg-gray-50 min-h-screen">
      {/* Header */}
      <div className="flex items-center justify-between border-b pb-4">
        <div className="flex items-center gap-4">
          <Button variant="ghost" onClick={() => navigate('/exams')} icon={<ChevronLeft size={24} />}>
            Back to Exams
          </Button>
          <h1 className="text-2xl font-bold text-blue-800">Create New Exam</h1>
        </div>
        <Button onClick={() => setCurrentStep('summary')} icon={<Save size={20} />} className="bg-gradient-to-r from-blue-500 to-indigo-600 hover:from-blue-600 hover:to-indigo-700">
          Review & Save
        </Button>
      </div>

      {/* Main Content */}
      <div className="grid grid-cols-5 gap-6">
        <div className="col-span-1">
          <nav className="space-y-1">
            {[
              { step: 'setup', icon: <Calendar size={20} />, label: 'Initial Setup' },
              { step: 'instructions', icon: <ListChecks size={20} />, label: 'Instructions' },
              { step: 'sections', icon: <BookOpen size={20} />, label: 'Sections' },
              { step: 'summary', icon: <CheckCircle size={20} />, label: 'Summary' },
            ].map(({ step, icon, label }) => (
              <button
                key={step}
                onClick={() => setCurrentStep(step)}
                className={`w-full flex items-center space-x-3 px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
                  currentStep === step 
                    ? 'bg-blue-500 text-white' 
                    : 'text-gray-600 hover:bg-gray-100'
                }`}
              >
                {icon}
                <span>{label}</span>
              </button>
            ))}
          </nav>
        </div>

        {/* Main Content Area */}
        <div className="col-span-4">
          {renderStep()}
        </div>
      </div>

      {loading && <Loader />}

      {error && (
        <div className="fixed inset-0 bg-gray-900 bg-opacity-50 flex items-center justify-center">
          <div className="bg-white p-6 rounded-lg shadow-lg">
            <AlertTriangle className="h-16 w-16 text-red-500 mx-auto mb-4" />
            <p className="text-center text-red-500">{error}</p>
            <Button onClick={() => setError(null)} className="mt-4 w-full">Close</Button>
          </div>
        </div>
      )}

      {flashcardMessage && (
        <Flashcard
          message={flashcardMessage}
          onClose={() => setFlashcardMessage(null)}
        />
      )}
    </div>
  );
}